<template>
  <div class="onboard-page">
    <!-- Background Elements -->
    <div class="page-grid"></div>
    <div class="page-orb orb-1"></div>
    <div class="page-orb orb-2"></div>

    <!-- Header -->
    <div class="onboard-header">
      <div class="header-content">
        <div class="header-brand">
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
            <path d="M12 2L14.5 9H21.5L15.5 13.5L18 20.5L12 16L6 20.5L8.5 13.5L2.5 9H9.5L12 2Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <span>Onboard New Club</span>
        </div>
        <button class="btn-back" @click="goBack">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M15.8333 10H4.16667M4.16667 10L10 15.8333M4.16667 10L10 4.16667" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <span>Back</span>
        </button>
      </div>
    </div>

    <v-container class="onboard-container">
      <!-- Hero Section -->
      <div class="onboard-hero">
        <h1 class="hero-title">Let's set up your club</h1>
        <p class="hero-subtitle">Guided steps. Saved to Firestore. Ready to go live.</p>
      </div>

      <!-- Main Content -->
      <div class="content-grid">
        <!-- Wizard Card -->
        <div class="wizard-card">
          <!-- Stepper Header -->
          <div class="stepper-header">
            <div
              v-for="(stepInfo, idx) in steps"
              :key="idx"
              :class="['step-item', {
                active: step === idx + 1,
                complete: step > idx + 1
              }]"
              @click="step = idx + 1"
            >
              <div class="step-number">
                <svg v-if="step > idx + 1" width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M13.3333 4L6 11.3333L2.66667 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span v-else>{{ idx + 1 }}</span>
              </div>
              <div class="step-label">{{ stepInfo.label }}</div>
            </div>
          </div>

          <!-- Step Content -->
          <div class="step-content">
            <!-- Step 1: Account -->
            <div v-if="step === 1" class="step-panel">
              <div class="panel-header">
                <h2>Account Credentials</h2>
                <p>Set up login credentials for this club's staff account</p>
              </div>

              <div class="form-field">
                <label>Club Email *</label>
                <div class="input-wrapper">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M2.5 6.66667L9.0755 11.0504C9.63533 11.4236 10.3647 11.4236 10.9245 11.0504L17.5 6.66667M4.16667 15H15.8333C16.7538 15 17.5 14.2538 17.5 13.3333V6.66667C17.5 5.74619 16.7538 5 15.8333 5H4.16667C3.24619 5 2.5 5.74619 2.5 6.66667V13.3333C2.5 14.2538 3.24619 15 4.16667 15Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <input v-model="customerEmail" type="email" placeholder="manager@fairviewcc.com" />
                </div>
              </div>

              <div class="form-field">
                <label>Temporary Password *</label>
                <div class="input-wrapper">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M5.83333 9.16667V6.66667C5.83333 4.36548 7.69881 2.5 10 2.5C12.3012 2.5 14.1667 4.36548 14.1667 6.66667V9.16667M10 11.6667V13.3333M6.66667 17.5H13.3333C14.2538 17.5 15 16.7538 15 15.8333V10.8333C15 9.91286 14.2538 9.16667 13.3333 9.16667H6.66667C5.74619 9.16667 5 9.91286 5 10.8333V15.8333C5 16.7538 5.74619 17.5 6.66667 17.5Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <input
                    v-model="customerPassword"
                    :type="showPwd ? 'text' : 'password'"
                    placeholder="Min 6 characters"
                  />
                  <button class="input-toggle" @click="showPwd = !showPwd" type="button">
                    <svg v-if="!showPwd" width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M2.5 10C2.5 10 5 4.16667 10 4.16667C15 4.16667 17.5 10 17.5 10C17.5 10 15 15.8333 10 15.8333C5 15.8333 2.5 10 2.5 10Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M10 12.5C11.3807 12.5 12.5 11.3807 12.5 10C12.5 8.61929 11.3807 7.5 10 7.5C8.61929 7.5 7.5 8.61929 7.5 10C7.5 11.3807 8.61929 12.5 10 12.5Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    <svg v-else width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M8.33333 8.33333C8.11232 8.69493 8 9.11497 8 9.58333C8 10.9641 9.11929 12.0833 10.5 12.0833C10.9684 12.0833 11.3884 11.971 11.75 11.75M14.95 14.95C13.5255 16.0358 11.8283 16.6667 10 16.6667C5 16.6667 2.5 10.8333 2.5 10.8333C3.47824 8.90809 4.80672 7.30515 6.38333 6.05M8.25 3.80833C8.82379 3.67954 9.40923 3.61484 10 3.61667C15 3.61667 17.5 9.45 17.5 9.45C17.0176 10.3891 16.446 11.2642 15.7917 12.0667M2.5 2.5L17.5 17.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </button>
                </div>
                <div class="password-strength">
                  <div class="strength-bar">
                    <div class="strength-fill" :style="{ width: passwordStrength + '%' }"></div>
                  </div>
                  <span class="strength-text">Password strength: {{ strengthLabel }}</span>
                </div>
              </div>

              <button class="btn-generate" type="button" @click="genPassword">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M2.25 9H15.75M9 2.25V15.75" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                Generate Strong Password
              </button>

              <div class="step-actions">
                <button class="btn-next" @click="step = 2" :disabled="!canStep1">
                  Continue
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path d="M6.75 13.5L11.25 9L6.75 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
              </div>
            </div>

            <!-- Step 2: Personal -->
            <div v-if="step === 2" class="step-panel">
              <div class="panel-header">
                <h2>Primary Contact</h2>
                <p>Add contact details for this club's manager</p>
              </div>

              <div class="form-field">
                <label>Full Name</label>
                <input v-model="fullName" type="text" placeholder="Jane Smith" />
              </div>

              <div class="form-field">
                <label>Phone Number</label>
                <input v-model="phone" type="tel" placeholder="+1 (555) 123-4567" />
              </div>

              <div class="form-field">
                <label>Website</label>
                <input v-model="website" type="url" placeholder="https://fairviewcc.com" />
              </div>

              <div class="step-actions">
                <button class="btn-back-step" @click="step = 1">
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path d="M11.25 13.5L6.75 9L11.25 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  Back
                </button>
                <button class="btn-next" @click="step = 3">
                  Continue
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path d="M6.75 13.5L11.25 9L6.75 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
              </div>
            </div>

            <!-- Step 3: Club Details -->
            <div v-if="step === 3" class="step-panel">
              <div class="panel-header">
                <h2>Club Details</h2>
                <p>Tell us about the club</p>
              </div>

              <div class="form-field">
                <label>Club Name *</label>
                <input v-model="businessName" type="text" placeholder="Fairview Country Club" />
              </div>

              <div class="form-field">
                <label>Club Description</label>
                <textarea v-model="businessDescription" rows="3" placeholder="Brief description of the club..."></textarea>
              </div>

              <div class="form-field">
                <label>Membership Tiers</label>
                <div class="language-tags">
                  <span
                    v-for="(tier, idx) in membershipTiers"
                    :key="idx"
                    class="language-tag"
                  >
                    {{ tier }}
                    <button type="button" @click="removeMembershipTier(idx)">✕</button>
                  </span>
                  <input
                    v-model="newMembershipTier"
                    type="text"
                    placeholder="e.g. Regular, Social, Junior"
                    @keydown.enter.prevent="addMembershipTier"
                    class="language-input"
                  />
                </div>
              </div>

              <div class="form-field">
                <label>Time Zone</label>
                <select v-model="timeZone" class="select-input">
                  <option value="" disabled>Select a time zone</option>
                  <option v-for="tz in timeZoneOptions" :key="tz" :value="tz">{{ tz }}</option>
                </select>
              </div>

              <div class="step-actions">
                <button class="btn-back-step" @click="step = 2">
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path d="M11.25 13.5L6.75 9L11.25 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  Back
                </button>
                <button class="btn-next" @click="step = 4">
                  Continue
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path d="M6.75 13.5L11.25 9L6.75 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
              </div>
            </div>

            <!-- Step 4: Address -->
            <div v-if="step === 4" class="step-panel">
              <div class="panel-header">
                <h2>Address</h2>
                <p>Where is this club located?</p>
              </div>

              <div class="form-field">
                <label>Street Address *</label>
                <input v-model="street" type="text" placeholder="123 Fairway Drive" />
              </div>

              <div class="form-row">
                <div class="form-field">
                  <label>City *</label>
                  <input v-model="city" type="text" placeholder="New York" />
                </div>
                <div class="form-field">
                  <label>State *</label>
                  <input v-model="state" type="text" placeholder="NY" />
                </div>
              </div>

              <div class="form-row">
                <div class="form-field">
                  <label>ZIP Code *</label>
                  <input v-model="zip" type="text" placeholder="10001" />
                </div>
                <div class="form-field">
                  <label>Country *</label>
                  <input v-model="country" type="text" placeholder="USA" />
                </div>
              </div>

              <div class="step-actions">
                <button class="btn-back-step" @click="step = 3">
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path d="M11.25 13.5L6.75 9L11.25 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  Back
                </button>
                <button class="btn-next" @click="step = 5">
                  Continue
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path d="M6.75 13.5L11.25 9L6.75 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
              </div>
            </div>

            <!-- Step 5: Billing & Preferences -->
            <div v-if="step === 5" class="step-panel">
              <div class="panel-header">
                <h2>Billing &amp; Preferences</h2>
                <p>Optional billing contact and customization</p>
              </div>

              <div class="expandable-section">
                <button class="expand-header" type="button" @click="toggleBilling">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M4.16667 6.66667H15.8333M4.16667 6.66667C3.24619 6.66667 2.5 6.10847 2.5 5.41667V3.75C2.5 3.05814 3.24619 2.5 4.16667 2.5H15.8333C16.7538 2.5 17.5 3.05814 17.5 3.75V5.41667C17.5 6.10847 16.7538 6.66667 15.8333 6.66667M4.16667 6.66667C3.24619 6.66667 2.5 7.22486 2.5 7.91667V16.25C2.5 16.9419 3.24619 17.5 4.16667 17.5H15.8333C16.7538 17.5 17.5 16.9419 17.5 16.25V7.91667C17.5 7.22486 16.7538 6.66667 15.8333 6.66667M4.16667 10H5.83333M4.16667 13.3333H5.83333" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <span>Billing Details (Optional)</span>
                  <svg :class="['expand-icon', { expanded: billingExpanded }]" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
                <div v-if="billingExpanded" class="expand-content">
                  <div class="form-field">
                    <label>Billing Contact Name</label>
                    <input v-model="billingName" type="text" placeholder="Jane Smith" />
                  </div>
                  <div class="form-field">
                    <label>Billing Email</label>
                    <input v-model="billingEmail" type="email" placeholder="billing@fairviewcc.com" />
                  </div>

                  <div v-if="cardSaved" class="card-saved-badge">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M16.6667 5L7.50004 14.1667L3.33337 10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    <span>{{ cardBrand }} •••• {{ cardLast4 }} saved</span>
                    <button type="button" class="btn-change-card" @click="changeCard">Change</button>
                  </div>

                  <template v-else>
                    <div class="form-field">
                      <label>Card Details</label>
                      <div v-if="stripeInitializing" class="stripe-card-loading">Loading secure card form…</div>
                      <div id="stripe-card-element" class="stripe-card-element" :class="{ hidden: stripeInitializing }"></div>
                      <div v-if="cardError" class="card-error">{{ cardError }}</div>
                    </div>

                    <button
                      type="button"
                      class="btn-save-card"
                      :disabled="!cardComplete || savingCard || stripeInitializing"
                      @click="saveCard"
                    >
                      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                        <path d="M2.25 4.5H15.75M2.25 9H15.75M2.25 13.5H9.75" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                      {{ savingCard ? 'Saving card…' : 'Save Card' }}
                    </button>
                  </template>

                  <div class="info-box">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <path d="M9 15.75C12.7279 15.75 15.75 12.7279 15.75 9C15.75 5.27208 12.7279 2.25 9 2.25C5.27208 2.25 2.25 5.27208 2.25 9C2.25 12.7279 5.27208 15.75 9 15.75Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M9 6V9M9 12H9.0075" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    <span>Card details go straight to Stripe and are never stored on our servers. You can skip billing during trials and add later.</span>
                  </div>
                </div>
              </div>

              <div class="expandable-section">
                <button class="expand-header" type="button" @click="prefsExpanded = !prefsExpanded">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M4.16667 10.8333C4.16667 10.8333 5.83333 7.5 10 7.5C14.1667 7.5 15.8333 10.8333 15.8333 10.8333M2.5 3.75L17.5 3.75M2.5 16.25L17.5 16.25" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <span>Preferences (Optional)</span>
                  <svg :class="['expand-icon', { expanded: prefsExpanded }]" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
                <div v-if="prefsExpanded" class="expand-content">
                  <div class="form-field">
                    <label>Theme Color</label>
                    <input v-model="colorPreference" type="text" placeholder="e.g., forest green, navy" />
                  </div>
                  <div class="form-field">
                    <label>Languages Supported</label>
                    <div class="language-tags">
                      <span
                        v-for="(lang, idx) in languages"
                        :key="idx"
                        class="language-tag"
                      >
                        {{ lang }}
                        <button type="button" @click="removeLanguage(idx)">✕</button>
                      </span>
                      <input
                        v-model="newLanguage"
                        type="text"
                        placeholder="Add language..."
                        @keydown.enter.prevent="addLanguage"
                        class="language-input"
                      />
                    </div>
                  </div>
                  <div class="form-field">
                    <label>Additional Notes</label>
                    <textarea v-model="additionalNotes" rows="3" placeholder="Any other information..."></textarea>
                  </div>
                </div>
              </div>

              <div class="step-actions">
                <button class="btn-back-step" @click="step = 4">
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path d="M11.25 13.5L6.75 9L11.25 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  Back
                </button>
                <button class="btn-next" @click="step = 6">
                  Continue
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path d="M6.75 13.5L11.25 9L6.75 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
              </div>
            </div>

            <!-- Step 6: Branding & Media -->
            <div v-if="step === 6" class="step-panel">
              <div class="panel-header">
                <h2>Branding &amp; Media</h2>
                <p>Logo and photos for the club (optional — can add later)</p>
              </div>

              <div class="form-field">
                <label>Club Logo</label>
                <div class="logo-upload-row">
                  <div class="logo-upload-preview" v-if="logoPreviewUrl">
                    <img :src="logoPreviewUrl" alt="Logo preview" />
                  </div>
                  <div class="logo-upload-preview logo-upload-preview-empty" v-else>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M4 16L8.586 11.414C8.96106 11.0391 9.46967 10.8284 10 10.8284C10.5303 10.8284 11.0389 11.0391 11.414 11.414L16 16M14 14L15.586 12.414C15.9611 12.0391 16.4697 11.8284 17 11.8284C17.5303 11.8284 18.0389 12.0391 18.414 12.414L20 14M4 6H20C21.1046 6 22 6.89543 22 8V16C22 17.1046 21.1046 18 20 18H4C2.89543 18 2 17.1046 2 16V8C2 6.89543 2.89543 6 4 6Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </div>
                  <div>
                    <input ref="logoInput" type="file" accept="image/*" style="display:none" @change="handleLogoFile" />
                    <button type="button" class="btn-generate" @click="$refs.logoInput.click()">
                      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                        <path d="M2.25 4.5H15.75M2.25 9H15.75M2.25 13.5H9.75" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                      {{ logoPreviewUrl ? 'Replace Logo' : 'Upload Logo' }}
                    </button>
                  </div>
                </div>
              </div>

              <div class="form-field">
                <label>Club Photos (rooms, bedrooms, grounds, etc.)</label>
                <div class="photo-upload-row">
                  <select v-model="newPhotoCategory" class="select-input" style="max-width: 200px;">
                    <option v-for="cat in photoCategoryOptions" :key="cat" :value="cat">{{ cat }}</option>
                  </select>
                  <input ref="photoInput" type="file" accept="image/*" multiple style="display:none" @change="handlePhotoFiles" />
                  <button type="button" class="btn-generate" @click="$refs.photoInput.click()">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <path d="M2.25 4.5H15.75M2.25 9H15.75M2.25 13.5H9.75" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    Add Photos
                  </button>
                </div>

                <div v-if="pendingPhotos.length" class="pending-photo-grid">
                  <div class="pending-photo" v-for="(p, idx) in pendingPhotos" :key="idx">
                    <img :src="p.previewUrl" :alt="p.category" />
                    <div class="pending-photo-overlay">
                      <span>{{ p.category }}</span>
                      <button type="button" @click="removePendingPhoto(idx)">✕</button>
                    </div>
                  </div>
                </div>
              </div>

              <div class="info-box">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M9 15.75C12.7279 15.75 15.75 12.7279 15.75 9C15.75 5.27208 12.7279 2.25 9 2.25C5.27208 2.25 2.25 5.27208 2.25 9C2.25 12.7279 5.27208 15.75 9 15.75Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M9 6V9M9 12H9.0075" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span>Logo and photos upload once the account is created below. You can also skip this and add them later from the customer's dashboard.</span>
              </div>

              <div class="step-actions">
                <button class="btn-back-step" @click="step = 5">
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path d="M11.25 13.5L6.75 9L11.25 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  Back
                </button>
                <button class="btn-next" @click="step = 7">
                  Review
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path d="M6.75 13.5L11.25 9L6.75 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
              </div>
            </div>

            <!-- Step 7: Review -->
            <div v-if="step === 7" class="step-panel">
              <div class="panel-header">
                <h2>Review &amp; Create</h2>
                <p>Verify all information before creating the account</p>
              </div>

              <div class="review-grid">
                <div class="review-section">
                  <div class="review-title">Account</div>
                  <div class="review-item">
                    <span class="review-label">Email:</span>
                    <span class="review-value">{{ customerEmail }}</span>
                  </div>
                </div>

                <div class="review-section">
                  <div class="review-title">Primary Contact</div>
                  <div class="review-item">
                    <span class="review-label">Name:</span>
                    <span class="review-value">{{ fullName || '—' }}</span>
                  </div>
                  <div class="review-item">
                    <span class="review-label">Phone:</span>
                    <span class="review-value">{{ phone || '—' }}</span>
                  </div>
                  <div class="review-item">
                    <span class="review-label">Website:</span>
                    <span class="review-value">{{ website || '—' }}</span>
                  </div>
                </div>

                <div class="review-section">
                  <div class="review-title">Club</div>
                  <div class="review-item">
                    <span class="review-label">Name:</span>
                    <span class="review-value">{{ businessName || '—' }}</span>
                  </div>
                  <div class="review-item">
                    <span class="review-label">Description:</span>
                    <span class="review-value">{{ businessDescription || '—' }}</span>
                  </div>
                  <div class="review-item">
                    <span class="review-label">Membership Tiers:</span>
                    <span class="review-value">{{ membershipTiers.length ? membershipTiers.join(', ') : '—' }}</span>
                  </div>
                  <div class="review-item">
                    <span class="review-label">Time Zone:</span>
                    <span class="review-value">{{ timeZone || '—' }}</span>
                  </div>
                </div>

                <div class="review-section">
                  <div class="review-title">Address</div>
                  <div class="review-item">
                    <span class="review-value">
                      {{ street }}, {{ city }}, {{ state }} {{ zip }}, {{ country }}
                    </span>
                  </div>
                </div>

                <div class="review-section">
                  <div class="review-title">Branding &amp; Media</div>
                  <div class="review-item">
                    <span class="review-label">Logo:</span>
                    <span class="review-value">{{ logoFile ? logoFile.name : 'None' }}</span>
                  </div>
                  <div class="review-item">
                    <span class="review-label">Photos:</span>
                    <span class="review-value">{{ pendingPhotos.length }} selected</span>
                  </div>
                </div>
              </div>

              <div v-if="message" :class="['message-alert', messageType]">
                <svg v-if="messageType === 'success'" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M16.6667 5L7.50004 14.1667L3.33337 10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <svg v-else width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M10 18.3333C14.6024 18.3333 18.3333 14.6024 18.3333 10C18.3333 5.39763 14.6024 1.66667 10 1.66667C5.39763 1.66667 1.66667 5.39763 1.66667 10C1.66667 14.6024 5.39763 18.3333 10 18.3333Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M10 6.66667V10M10 13.3333H10.0083" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span>{{ message }}</span>
              </div>

              <div class="step-actions">
                <button class="btn-back-step" @click="step = 6">
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path d="M11.25 13.5L6.75 9L11.25 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  Back
                </button>
                <button class="btn-create" @click="submitOnboarding" :disabled="submitting">
                  <span v-if="!submitting">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <path d="M15 4.5L6.75 12.75L3 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    Create Club Account
                  </span>
                  <span v-else class="loader-dots">
                    <span></span><span></span><span></span>
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Tips Sidebar -->
        <div class="tips-card">
          <div class="tips-header">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M15 12C15 13.6569 13.6569 15 12 15C10.3431 15 9 13.6569 9 12C9 10.3431 10.3431 9 12 9C13.6569 9 15 10.3431 15 12Z" stroke="currentColor" stroke-width="2"/>
              <path d="M12 2V4M12 20V22M4 12H2M6.31412 6.31412L4.8999 4.8999M17.6859 6.31412L19.1001 4.8999M6.31412 17.69L4.8999 19.1042M17.6859 17.69L19.1001 19.1042M22 12H20" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
            <span>Success Tips</span>
          </div>

          <div class="tips-list">
            <div class="tip-item">
              <div :class="['tip-icon', { active: canStep1 }]">
                <svg v-if="canStep1" width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M15 4.5L6.75 12.75L3 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span v-else>1</span>
              </div>
              <span class="tip-text">Email &amp; strong password set</span>
            </div>

            <div class="tip-item">
              <div :class="['tip-icon', { active: businessName }]">
                <svg v-if="businessName" width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M15 4.5L6.75 12.75L3 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span v-else>2</span>
              </div>
              <span class="tip-text">Club name provided</span>
            </div>

            <div class="tip-item">
              <div :class="['tip-icon', { active: addressComplete }]">
                <svg v-if="addressComplete" width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M15 4.5L6.75 12.75L3 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span v-else>3</span>
              </div>
              <span class="tip-text">Address complete</span>
            </div>

            <div class="tip-item">
              <div class="tip-icon tip-icon-optional">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M9 4.5V9L12 10.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M9 15.75C12.7279 15.75 15.75 12.7279 15.75 9C15.75 5.27208 12.7279 2.25 9 2.25C5.27208 2.25 2.25 5.27208 2.25 9C2.25 12.7279 5.27208 15.75 9 15.75Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
              <span class="tip-text">Choose theme &amp; languages</span>
            </div>
          </div>

          <div class="tips-footer">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M9 15.75C12.7279 15.75 15.75 12.7279 15.75 9C15.75 5.27208 12.7279 2.25 9 2.25C5.27208 2.25 2.25 5.27208 2.25 9C2.25 12.7279 5.27208 15.75 9 15.75Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M9 6V9M9 12H9.0075" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span>Billing is optional at signup — invoicing can be set up later</span>
          </div>
        </div>
      </div>
    </v-container>
  </div>
</template>

<script>
import { functions, db } from '../firebase'
import { httpsCallable } from 'firebase/functions'
import { doc, setDoc, collection, addDoc, serverTimestamp } from 'firebase/firestore'
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage'
import { loadStripe } from '@stripe/stripe-js'
import router from '../router'

// Safe to expose publicly — this is Stripe's publishable key, not the
// secret key. Same Stripe ACCOUNT as Schoolz Connect (publishable/secret
// keys are scoped to the account, not per-product), so this is the exact
// same live key value Schoolz Connect uses.
const STRIPE_PUBLISHABLE_KEY = 'pk_live_51R6zeuCyXNASTSVWF468xwitVpX6OXKslKNFqWM7jI3WNtqWPtBu961zobtteyYg13GuklADj60mbhCV5Vwc4VhZ00HuuW5AKL'

export default {
  name: 'StaffOnboardCustomer',
  data() {
    return {
      step: 1,
      steps: [
        { label: 'Account' },
        { label: 'Contact' },
        { label: 'Club' },
        { label: 'Address' },
        { label: 'Billing' },
        { label: 'Branding' },
        { label: 'Review' }
      ],

      // Form fields
      customerEmail: '',
      customerPassword: '',
      showPwd: false,
      fullName: '',
      phone: '',
      website: '',
      businessName: '',
      businessDescription: '',
      street: '',
      city: '',
      state: '',
      zip: '',
      country: '',
      billingName: '',
      billingEmail: '',
      // Stripe state (the Stripe SDK objects themselves — stripe, elements,
      // cardElement — are intentionally kept OFF this reactive data object;
      // see mounted()/methods below. Only plain values live here.)
      stripeCustomerId: '',
      stripeClientSecret: '',
      stripePaymentMethodId: '',
      cardBrand: '',
      cardLast4: '',
      cardError: '',
      cardComplete: false,
      cardSaved: false,
      stripeInitializing: false,
      savingCard: false,
      colorPreference: '',
      languages: [],
      newLanguage: '',
      additionalNotes: '',
      membershipTiers: [],
      newMembershipTier: '',
      timeZone: '',
      timeZoneOptions: [
        'Eastern Time (US & Canada)',
        'Central Time (US & Canada)',
        'Mountain Time (US & Canada)',
        'Pacific Time (US & Canada)',
        'Alaska Time',
        'Hawaii Time',
        'Atlantic Time (Canada)',
        'UTC'
      ],

      // Branding & media (uploaded to Storage after account creation)
      logoFile: null,
      logoPreviewUrl: '',
      pendingPhotos: [], // [{ file, previewUrl, category }]
      newPhotoCategory: 'Club Room',
      photoCategoryOptions: ['Club Room', 'Bedroom', 'Dining', 'Grounds', 'Exterior', 'Other'],

      // UI state
      submitting: false,
      message: '',
      messageType: 'success',
      billingExpanded: false,
      prefsExpanded: false
    }
  },
  computed: {
    passwordStrength() {
      const v = this.customerPassword || ''
      let score = 0
      if (v.length >= 6) score += 25
      if (/[A-Z]/.test(v)) score += 25
      if (/[0-9]/.test(v)) score += 25
      if (/[^A-Za-z0-9]/.test(v)) score += 25
      return score
    },
    strengthLabel() {
      const s = this.passwordStrength
      if (s === 0) return 'None'
      if (s <= 25) return 'Weak'
      if (s <= 50) return 'Fair'
      if (s <= 75) return 'Good'
      return 'Strong'
    },
    canStep1() {
      return this.customerEmail &&
             /.+@.+\..+/.test(this.customerEmail) &&
             this.customerPassword &&
             this.customerPassword.length >= 6
    },
    addressComplete() {
      return this.street && this.city && this.state && this.zip && this.country
    }
  },
  methods: {
    genPassword() {
      const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%&*?'
      let out = ''
      for (let i = 0; i < 12; i++) {
        out += chars[Math.floor(Math.random() * chars.length)]
      }
      this.customerPassword = out
    },
    addLanguage() {
      if (this.newLanguage && !this.languages.includes(this.newLanguage)) {
        this.languages.push(this.newLanguage)
        this.newLanguage = ''
      }
    },
    removeLanguage(idx) {
      this.languages.splice(idx, 1)
    },
    addMembershipTier() {
      const v = (this.newMembershipTier || '').trim()
      if (v && !this.membershipTiers.includes(v)) {
        this.membershipTiers.push(v)
        this.newMembershipTier = ''
      }
    },
    removeMembershipTier(idx) {
      this.membershipTiers.splice(idx, 1)
    },
    handleLogoFile(e) {
      const file = e.target.files?.[0]
      if (!file) return
      this.logoFile = file
      this.logoPreviewUrl = URL.createObjectURL(file)
    },
    handlePhotoFiles(e) {
      const files = Array.from(e.target.files || [])
      files.forEach(file => {
        this.pendingPhotos.push({
          file,
          previewUrl: URL.createObjectURL(file),
          category: this.newPhotoCategory
        })
      })
      if (e.target) e.target.value = ''
    },
    removePendingPhoto(idx) {
      this.pendingPhotos.splice(idx, 1)
    },

    // ---------------- Stripe ----------------

    async toggleBilling() {
      this.billingExpanded = !this.billingExpanded
      if (this.billingExpanded && !this.cardSaved && !this._stripe) {
        await this.initStripe()
      }
    },

    async initStripe() {
      this.stripeInitializing = true
      this.cardError = ''
      try {
        const [stripe, setupResult] = await Promise.all([
          loadStripe(STRIPE_PUBLISHABLE_KEY),
          httpsCallable(functions, 'createStripeSetupIntent')({
            email: this.customerEmail,
            name: this.billingName || this.fullName,
            businessName: this.businessName,
            address: {
              street: this.street,
              city: this.city,
              state: this.state,
              zip: this.zip,
              country: this.country
            }
          })
        ])

        if (!stripe) {
          throw new Error('Stripe failed to load')
        }

        this._stripe = stripe
        this.stripeCustomerId = setupResult.data.customerId
        this.stripeClientSecret = setupResult.data.clientSecret

        this._elements = this._stripe.elements()
        this._cardElement = this._elements.create('card', {
          style: {
            base: {
              color: '#F3F5F8',
              fontFamily: 'Inter, -apple-system, sans-serif',
              fontSize: '15px',
              '::placeholder': { color: 'rgba(243, 245, 248, 0.4)' },
              iconColor: 'rgba(243, 245, 248, 0.6)'
            },
            invalid: {
              color: '#F87171',
              iconColor: '#F87171'
            }
          }
        })

        this._cardElement.on('change', (event) => {
          this.cardComplete = event.complete
          this.cardError = event.error ? event.error.message : ''
        })

        await this.$nextTick()
        this._cardElement.mount('#stripe-card-element')
      } catch (e) {
        console.error('Error initializing Stripe:', e)
        this.cardError = 'Could not load the secure card form. Please try again.'
      } finally {
        this.stripeInitializing = false
      }
    },

    async saveCard() {
      if (!this._stripe || !this._cardElement || !this.cardComplete) return

      this.savingCard = true
      this.cardError = ''
      try {
        const { setupIntent, error } = await this._stripe.confirmCardSetup(
          this.stripeClientSecret,
          {
            payment_method: {
              card: this._cardElement,
              billing_details: {
                name: this.billingName || this.fullName || undefined,
                email: this.billingEmail || this.customerEmail || undefined
              }
            }
          }
        )

        if (error) {
          this.cardError = error.message || 'Could not save this card. Please check the details and try again.'
          return
        }

        this.stripePaymentMethodId = setupIntent.payment_method

        const confirmResult = await httpsCallable(functions, 'confirmStripePaymentMethod')({
          customerId: this.stripeCustomerId,
          paymentMethodId: this.stripePaymentMethodId
        })

        this.cardBrand = this.formatCardBrand(confirmResult.data.brand)
        this.cardLast4 = confirmResult.data.last4
        this.cardSaved = true

        this._cardElement.unmount()
      } catch (e) {
        console.error('Error saving card:', e)
        this.cardError = 'Something went wrong saving this card. Please try again.'
      } finally {
        this.savingCard = false
      }
    },

    changeCard() {
      this.cardSaved = false
      this.cardComplete = false
      this.cardError = ''
      this.stripePaymentMethodId = ''
      this.cardBrand = ''
      this.cardLast4 = ''
      this._cardElement = null
      this._elements = null
      this.$nextTick(() => this.initStripe())
    },

    formatCardBrand(brand) {
      const names = {
        visa: 'Visa',
        mastercard: 'Mastercard',
        amex: 'American Express',
        discover: 'Discover',
        diners: 'Diners Club',
        jcb: 'JCB',
        unionpay: 'UnionPay'
      }
      return names[brand] || (brand ? brand.charAt(0).toUpperCase() + brand.slice(1) : 'Card')
    },

    async submitOnboarding() {
      if (!this.customerEmail || !this.customerPassword || !this.businessName || !this.addressComplete) {
        this.message = 'Please complete all required fields'
        this.messageType = 'error'
        return
      }

      this.submitting = true
      try {
        // Account creation and Firestore writes happen entirely
        // server-side — staff's own session is never touched. (The old
        // client-side createUserWithEmailAndPassword approach silently
        // swaps staff's browser session over to the new customer account
        // the moment it runs — a real bug, avoided entirely here.)
        const result = await httpsCallable(functions, 'createCustomerAccount')({
          email: this.customerEmail,
          password: this.customerPassword,
          fullName: this.fullName,
          phone: this.phone,
          website: this.website,
          businessName: this.businessName,
          businessDescription: this.businessDescription,
          address: {
            street: this.street,
            city: this.city,
            state: this.state,
            zip: this.zip,
            country: this.country
          },
          membershipTiers: this.membershipTiers,
          timeZone: this.timeZone,
          billingName: this.billingName,
          stripeCustomerId: this.stripeCustomerId || null,
          stripePaymentMethodId: this.stripePaymentMethodId || null,
          cardBrand: this.cardBrand || null,
          cardLast4: this.cardLast4 || null,
          colorPreference: this.colorPreference,
          languages: this.languages,
          additionalNotes: this.additionalNotes
        })

        // NOTE: assumes createCustomerAccount returns the new customer's
        // uid on result.data.uid — update this key if the Cloud Function's
        // actual response shape differs.
        const newUid = result?.data?.uid || result?.data?.customerId

        if (newUid && (this.logoFile || this.pendingPhotos.length)) {
          try {
            await this.uploadBrandingAssets(newUid)
          } catch (uploadErr) {
            // Account creation already succeeded — don't fail the whole
            // flow over a branding upload issue, just let staff know.
            console.error('Error uploading branding assets:', uploadErr)
            this.message = '✅ Club account created — but logo/photos failed to upload. Add them from the customer\'s dashboard later.'
            this.messageType = 'error'
            setTimeout(() => router.push('/staff-dashboard'), 2500)
            return
          }
        }

        this.message = '✅ Club account created successfully!'
        this.messageType = 'success'

        setTimeout(() => {
          router.push('/staff-dashboard')
        }, 2000)
      } catch (error) {
        console.error('Error onboarding club:', error)
        this.message = '❌ Failed to create account: ' + (error.message || 'unknown error')
        this.messageType = 'error'
      } finally {
        this.submitting = false
      }
    },
    async uploadBrandingAssets(customerUid) {
      const storage = getStorage()

      if (this.logoFile) {
        const path = `customers/${customerUid}/branding/logo-${Date.now()}-${this.logoFile.name}`
        const fileRef = storageRef(storage, path)
        await uploadBytes(fileRef, this.logoFile)
        const logoUrl = await getDownloadURL(fileRef)
        const brandingUpdate = { branding: { logoUrl, logoStoragePath: path, colorPreference: this.colorPreference } }
        // Mirror to both docs — createCustomerAccount just created users/{uid}
        // and customers/{uid} identically, but this upload step only touched
        // customers/{uid} until now. Without this, the logo would look
        // uploaded on the customer's own Settings page but never show up on
        // CustomerDetailView.vue (which reads from users/{uid}) — same bug
        // just fixed in CustomerSettingsView.vue's handleLogoFile/removeLogo.
        await setDoc(doc(db, 'customers', customerUid), brandingUpdate, { merge: true })
        await setDoc(doc(db, 'users', customerUid), brandingUpdate, { merge: true })
      }

      for (const p of this.pendingPhotos) {
        const path = `customers/${customerUid}/photos/${Date.now()}-${p.file.name}`
        const fileRef = storageRef(storage, path)
        await uploadBytes(fileRef, p.file)
        const downloadURL = await getDownloadURL(fileRef)
        await addDoc(collection(db, 'customers', customerUid, 'photos'), {
          fileName: p.file.name,
          storagePath: path,
          downloadURL,
          category: p.category,
          uploadedAt: serverTimestamp()
        })
      }
    },
    goBack() {
      router.push('/staff-dashboard')
    }
  },
  beforeDestroy() {
    if (this._cardElement) {
      try { this._cardElement.unmount() } catch (e) { /* already unmounted */ }
    }
  }
}
</script>

<style scoped>
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;600;700&family=Montserrat:wght@400;500;600;700&display=swap');

  /* ==================== PAGE BACKGROUND ==================== */
  .onboard-page {
    --bg-page: #16241d;
    --bg-alt: #1c3128;
    --bg-card: #203a2f;
    --bg-card-hover: #274637;
    --border-subtle: rgba(255, 255, 255, 0.09);
    --border-gold: rgba(212, 175, 55, 0.35);
    --text-primary: #F5F6F4;
    --text-secondary: rgba(245, 246, 244, 0.68);
    --text-muted: rgba(245, 246, 244, 0.48);
    --gold: #d4af37;
    --gold-light: #e0c27a;
    --gold-dark: #a8862c;

    min-height: 100vh;
    background: linear-gradient(160deg, #1a2e26 0%, #2d4a3e 55%, #16241d 100%);
    position: relative;
    font-family: 'Montserrat', -apple-system, BlinkMacSystemFont, sans-serif;
    color: var(--text-primary);
    padding-bottom: 80px;
    overflow-x: hidden;
  }

  .onboard-page .hero-title,
  .onboard-page .panel-header h2 {
    font-family: 'Playfair Display', 'Georgia', serif;
  }

  .page-grid {
    position: fixed;
    inset: 0;
    background-image:
      linear-gradient(to right, rgba(255, 255, 255, 0.05) 1px, transparent 1px),
      linear-gradient(to bottom, rgba(255, 255, 255, 0.05) 1px, transparent 1px);
    background-size: 60px 60px;
    mask-image: radial-gradient(ellipse 70% 60% at 50% 50%, black 20%, transparent 70%);
    pointer-events: none;
    z-index: 0;
  }

  .page-orb {
    position: fixed;
    border-radius: 50%;
    filter: blur(150px);
    opacity: 0.2;
    pointer-events: none;
    z-index: 0;
  }

  .orb-1 {
    width: 600px;
    height: 600px;
    background: radial-gradient(circle, rgba(212, 175, 55, 0.45), transparent);
    top: -200px;
    left: -200px;
  }

  .orb-2 {
    width: 500px;
    height: 500px;
    background: radial-gradient(circle, rgba(91, 141, 191, 0.28), transparent);
    bottom: -150px;
    right: -150px;
  }

  /* ==================== HEADER ==================== */
  .onboard-header {
    position: sticky;
    top: 0;
    z-index: 100;
    background: rgba(22, 36, 29, 0.9);
    backdrop-filter: blur(12px);
    border-bottom: 1px solid var(--border-subtle);
  }

  .header-content {
    max-width: 1400px;
    margin: 0 auto;
    padding: 16px 24px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .header-brand {
    display: flex;
    align-items: center;
    gap: 12px;
    color: var(--gold-light);
    font-size: 17px;
    font-weight: 700;
  }

  .btn-back {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid var(--border-subtle);
    border-radius: 8px;
    color: var(--text-secondary);
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .btn-back:hover {
    border-color: var(--border-gold);
    color: var(--text-primary);
  }

  /* ==================== HERO ==================== */
  .onboard-container {
    position: relative;
    z-index: 2;
    max-width: 1200px;
    padding-top: 40px;
  }

  .onboard-hero {
    text-align: center;
    margin-bottom: 36px;
  }

  .hero-title {
    font-size: clamp(26px, 4vw, 36px);
    font-weight: 700;
    margin-bottom: 8px;
  }

  .hero-subtitle {
    color: var(--text-secondary);
    font-size: 15px;
  }

  /* ==================== CONTENT GRID ==================== */
  .content-grid {
    display: grid;
    grid-template-columns: 1fr 300px;
    gap: 24px;
    align-items: start;
  }

  .wizard-card {
    background: var(--bg-card);
    border: 1px solid var(--border-subtle);
    border-radius: 20px;
    overflow: hidden;
  }

  /* ==================== STEPPER ==================== */
  .stepper-header {
    display: flex;
    border-bottom: 1px solid var(--border-subtle);
    overflow-x: auto;
  }

  .step-item {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 18px 12px;
    cursor: pointer;
    transition: all 0.2s ease;
    min-width: 90px;
    border-bottom: 2px solid transparent;
  }

  .step-item.active {
    border-bottom-color: var(--gold);
  }

  .step-number {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: var(--bg-alt);
    border: 1px solid var(--border-subtle);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    font-weight: 700;
    color: var(--text-muted);
    transition: all 0.2s ease;
  }

  .step-item.active .step-number {
    background: linear-gradient(150deg, var(--gold-light), var(--gold));
    border-color: var(--gold);
    color: #1a2e26;
  }

  .step-item.complete .step-number {
    background: rgba(79, 143, 111, 0.2);
    border-color: #4F8F6F;
    color: #8fcfad;
  }

  .step-label {
    font-size: 12px;
    font-weight: 600;
    color: var(--text-muted);
    text-align: center;
  }

  .step-item.active .step-label {
    color: var(--text-primary);
  }

  /* ==================== STEP CONTENT ==================== */
  .step-content {
    padding: 32px;
  }

  .panel-header {
    margin-bottom: 24px;
  }

  .panel-header h2 {
    font-size: 22px;
    font-weight: 700;
    margin-bottom: 6px;
  }

  .panel-header p {
    color: var(--text-secondary);
    font-size: 14px;
    margin: 0;
  }

  .form-field {
    margin-bottom: 20px;
  }

  .form-field label {
    display: block;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--text-muted);
    margin-bottom: 8px;
  }

  .form-field input,
  .form-field textarea {
    width: 100%;
    padding: 12px 16px;
    background: var(--bg-alt);
    border: 1px solid var(--border-subtle);
    border-radius: 10px;
    color: var(--text-primary);
    font-family: 'Montserrat', sans-serif;
    font-size: 15px;
    outline: none;
    transition: all 0.2s ease;
    resize: vertical;
  }

  .form-field input:focus,
  .form-field textarea:focus {
    border-color: var(--gold);
    box-shadow: 0 0 0 3px rgba(212, 175, 55, 0.12);
  }

  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }

  .input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
  }

  .input-wrapper svg {
    position: absolute;
    left: 14px;
    color: var(--text-muted);
    pointer-events: none;
  }

  .input-wrapper input {
    padding-left: 44px;
  }

  .input-toggle {
    position: absolute;
    right: 12px;
    background: transparent;
    border: none;
    color: var(--text-muted);
    cursor: pointer;
    display: flex;
  }

  .password-strength {
    margin-top: 10px;
  }

  .strength-bar {
    height: 5px;
    background: var(--bg-alt);
    border-radius: 999px;
    overflow: hidden;
    margin-bottom: 6px;
  }

  .strength-fill {
    height: 100%;
    background: linear-gradient(90deg, #c85a50, var(--gold), #4F8F6F);
    transition: width 0.3s ease;
  }

  .strength-text {
    font-size: 12px;
    color: var(--text-muted);
  }

  .btn-generate {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 16px;
    background: rgba(212, 175, 55, 0.08);
    border: 1px solid var(--border-gold);
    border-radius: 9px;
    color: var(--gold-light);
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    margin-bottom: 8px;
    transition: all 0.2s ease;
  }

  .btn-generate:hover {
    background: rgba(212, 175, 55, 0.14);
  }

  /* ==================== EXPANDABLE SECTIONS ==================== */
  .expandable-section {
    border: 1px solid var(--border-subtle);
    border-radius: 12px;
    margin-bottom: 16px;
    overflow: hidden;
  }

  .expand-header {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 14px 16px;
    background: var(--bg-alt);
    border: none;
    color: var(--text-primary);
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
  }

  .expand-header span {
    flex: 1;
    text-align: left;
  }

  .expand-icon {
    transition: transform 0.2s ease;
  }

  .expand-icon.expanded {
    transform: rotate(180deg);
  }

  .expand-content {
    padding: 20px 16px;
  }

  .info-box {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 12px 14px;
    background: rgba(212, 175, 55, 0.08);
    border: 1px solid var(--border-gold);
    border-radius: 10px;
    color: var(--gold-light);
    font-size: 12.5px;
    line-height: 1.5;
  }

  /* ==================== STRIPE CARD ELEMENT ==================== */
  .stripe-card-element {
    padding: 14px 16px;
    background: var(--bg-alt);
    border: 1px solid var(--border-subtle);
    border-radius: 10px;
    transition: border-color 0.2s ease;
  }

  .stripe-card-element.hidden {
    display: none;
  }

  .stripe-card-element.StripeElement--focus {
    border-color: var(--gold);
  }

  .stripe-card-element.StripeElement--invalid {
    border-color: #F87171;
  }

  .stripe-card-loading {
    padding: 14px 16px;
    background: var(--bg-alt);
    border: 1px solid var(--border-subtle);
    border-radius: 10px;
    color: var(--text-muted);
    font-size: 13px;
  }

  .card-error {
    margin-top: 8px;
    color: #F87171;
    font-size: 12.5px;
  }

  .btn-save-card {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-top: 14px;
    padding: 11px 20px;
    background: linear-gradient(150deg, var(--gold-light), var(--gold));
    border: none;
    border-radius: 10px;
    color: #1A1408;
    font-size: 13.5px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .btn-save-card:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 6px 18px rgba(212, 175, 55, 0.3);
  }

  .btn-save-card:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .card-saved-badge {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 14px;
    background: rgba(79, 143, 111, 0.12);
    border: 1px solid rgba(79, 143, 111, 0.35);
    border-radius: 10px;
    color: #6FA989;
    font-size: 13.5px;
    font-weight: 600;
    margin-bottom: 12px;
  }

  .card-saved-badge svg {
    flex-shrink: 0;
  }

  .card-saved-badge span {
    flex: 1;
  }

  .btn-change-card {
    background: transparent;
    border: 1px solid rgba(79, 143, 111, 0.4);
    border-radius: 8px;
    padding: 5px 12px;
    color: #6FA989;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .btn-change-card:hover {
    background: rgba(79, 143, 111, 0.15);
  }

  .language-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    padding: 10px;
    background: var(--bg-alt);
    border: 1px solid var(--border-subtle);
    border-radius: 10px;
  }

  .language-tag {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 5px 10px;
    background: rgba(212, 175, 55, 0.12);
    border-radius: 999px;
    font-size: 12.5px;
    color: var(--gold-light);
  }

  .language-tag button {
    background: transparent;
    border: none;
    color: var(--gold-light);
    cursor: pointer;
    font-size: 11px;
  }

  .language-input {
    flex: 1;
    min-width: 100px;
    border: none !important;
    background: transparent !important;
    padding: 6px !important;
  }

  /* ==================== SELECT INPUT ==================== */
  .select-input {
    width: 100%;
    padding: 12px 16px;
    background: var(--bg-alt);
    border: 1px solid var(--border-subtle);
    border-radius: 10px;
    color: var(--text-primary);
    font-family: 'Montserrat', sans-serif;
    font-size: 15px;
    outline: none;
    transition: all 0.2s ease;
    cursor: pointer;
  }

  .select-input:focus {
    border-color: var(--gold);
    box-shadow: 0 0 0 3px rgba(212, 175, 55, 0.12);
  }

  .select-input option {
    background: var(--bg-card);
    color: var(--text-primary);
  }

  /* ==================== LOGO & PHOTO UPLOAD ==================== */
  .logo-upload-row {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .logo-upload-preview {
    width: 72px;
    height: 72px;
    border-radius: 12px;
    overflow: hidden;
    background: var(--bg-alt);
    border: 1px solid var(--border-subtle);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: var(--text-muted);
  }

  .logo-upload-preview img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .logo-upload-preview-empty {
    border-style: dashed;
  }

  .photo-upload-row {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    margin-bottom: 14px;
  }

  .pending-photo-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
    gap: 10px;
  }

  .pending-photo {
    position: relative;
    aspect-ratio: 1;
    border-radius: 10px;
    overflow: hidden;
    background: var(--bg-alt);
    border: 1px solid var(--border-subtle);
  }

  .pending-photo img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .pending-photo-overlay {
    position: absolute;
    left: 0; right: 0; bottom: 0;
    background: linear-gradient(0deg, rgba(0,0,0,0.75), transparent);
    color: #fff;
    font-size: 11px;
    padding: 4px 6px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .pending-photo-overlay button {
    background: transparent;
    border: none;
    color: #fff;
    cursor: pointer;
    font-size: 11px;
    padding: 0 2px;
  }

  /* ==================== REVIEW ==================== */
  .review-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 16px;
    margin-bottom: 24px;
  }

  .review-section {
    padding: 16px;
    background: var(--bg-alt);
    border-radius: 12px;
  }

  .review-title {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--gold-light);
    margin-bottom: 10px;
  }

  .review-item {
    display: flex;
    gap: 6px;
    font-size: 13.5px;
    margin-bottom: 6px;
    flex-wrap: wrap;
  }

  .review-label {
    color: var(--text-muted);
  }

  .review-value {
    color: var(--text-primary);
    font-weight: 500;
  }

  .message-alert {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 14px 16px;
    border-radius: 10px;
    font-size: 14px;
    margin-bottom: 20px;
  }

  .message-alert.success {
    background: rgba(79, 143, 111, 0.12);
    border: 1px solid rgba(79, 143, 111, 0.35);
    color: #8fcfad;
  }

  .message-alert.error {
    background: rgba(200, 90, 80, 0.12);
    border: 1px solid rgba(200, 90, 80, 0.35);
    color: #eba79c;
  }

  /* ==================== STEP ACTIONS ==================== */
  .step-actions {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    margin-top: 8px;
  }

  .btn-back-step {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 12px 20px;
    background: transparent;
    border: 1px solid var(--border-subtle);
    border-radius: 10px;
    color: var(--text-secondary);
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .btn-back-step:hover {
    border-color: var(--border-gold);
    color: var(--text-primary);
  }

  .btn-next,
  .btn-create {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 24px;
    margin-left: auto;
    background: linear-gradient(150deg, var(--gold-light), var(--gold));
    border: none;
    border-radius: 10px;
    color: #1a2e26;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .btn-next:hover:not(:disabled),
  .btn-create:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(212, 175, 55, 0.3);
  }

  .btn-next:disabled,
  .btn-create:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .loader-dots {
    display: flex;
    gap: 4px;
  }

  .loader-dots span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #1a2e26;
    animation: dot-pulse 1s ease-in-out infinite;
  }

  .loader-dots span:nth-child(2) { animation-delay: 0.15s; }
  .loader-dots span:nth-child(3) { animation-delay: 0.3s; }

  @keyframes dot-pulse {
    0%, 80%, 100% { opacity: 0.3; }
    40% { opacity: 1; }
  }

  /* ==================== TIPS SIDEBAR ==================== */
  .tips-card {
    background: var(--bg-card);
    border: 1px solid var(--border-subtle);
    border-radius: 18px;
    padding: 24px;
    position: sticky;
    top: 88px;
  }

  .tips-header {
    display: flex;
    align-items: center;
    gap: 10px;
    color: var(--gold-light);
    font-size: 15px;
    font-weight: 700;
    margin-bottom: 20px;
  }

  .tips-list {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-bottom: 20px;
  }

  .tip-item {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .tip-icon {
    width: 26px;
    height: 26px;
    flex-shrink: 0;
    border-radius: 50%;
    background: var(--bg-alt);
    border: 1px solid var(--border-subtle);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 700;
    color: var(--text-muted);
  }

  .tip-icon.active {
    background: rgba(79, 143, 111, 0.2);
    border-color: #4F8F6F;
    color: #8fcfad;
  }

  .tip-icon-optional {
    color: var(--gold-light);
    border-color: var(--border-gold);
  }

  .tip-text {
    font-size: 13.5px;
    color: var(--text-secondary);
  }

  .tips-footer {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding-top: 16px;
    border-top: 1px solid var(--border-subtle);
    font-size: 12px;
    color: var(--text-muted);
    line-height: 1.5;
  }

  /* ==================== RESPONSIVE ==================== */
  @media (max-width: 900px) {
    .content-grid {
      grid-template-columns: 1fr;
    }

    .tips-card {
      position: static;
    }
  }

  @media (max-width: 600px) {
    .step-content {
      padding: 24px 20px;
    }

    .form-row {
      grid-template-columns: 1fr;
    }

    .step-label {
      display: none;
    }
  }
</style>